import type { CourseWithoutBlocks } from '@/types/course'
import type { ModerationState } from '@/features/moderation/types'
import { Label } from '@teach/ui'
import { SmoothHeight } from '@/components/common/SmoothHeight'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { DraftChangesSection } from './DraftChangesSection'
import { ReviewStatusPanel } from './ReviewStatusPanel'
import { liveStatusKey } from './liveStatusKey'
import type { useCourseUpdate } from './useCourseUpdate'
import type { ViolationGo } from './violationPlace'

interface Props {
  course: CourseWithoutBlocks
  moderation: ModerationState
  update: ReturnType<typeof useCourseUpdate>
  isCourseAuthor: boolean
  accessBusy: boolean
  accessRetryLabel: string | null
  onNavigate: () => void
  onGo?: (target: ViolationGo) => void
}

/**
 * Состояние проверки и правок курса в окне публикации. У живого курса
 * изменения → проверка → итог сменяют друг друга в ОДНОМ блоке, высота и
 * содержимое перетекают плавно (`SmoothHeight`, решение заказчика).
 */
export const UpdateStatusBlock = ({
  course,
  moderation,
  update,
  isCourseAuthor,
  accessBusy,
  accessRetryLabel,
  onNavigate,
  onGo,
}: Props) => {
  const m = useMessages(messages)
  // Отказ проверки обновления: кнопки — прямо в карточке отказа, без отдельной
  // «Есть неопубликованные изменения» (решение заказчика: одно и то же трижды).
  const merged =
    moderation.review?.status === 'rejected' &&
    moderation.visibility !== 'unpublished' &&
    moderation.hasUnsubmittedChanges
  const changes =
    moderation.draftId !== null ? (
      <DraftChangesSection
        course={course}
        update={update}
        moderation={moderation}
        isCourseAuthor={isCourseAuthor}
        accessBusy={accessBusy}
        accessRetryLabel={accessRetryLabel}
        bare={merged}
      />
    ) : null
  const status = (
    <>
      <ReviewStatusPanel
        review={moderation.review}
        visibility={moderation.visibility}
        hasUnsubmittedChanges={moderation.hasUnsubmittedChanges}
        course={course}
        onNavigate={onNavigate}
        onGo={onGo}
        actions={merged ? changes : undefined}
      />
      {merged ? null : changes}
    </>
  )
  const statusKey = liveStatusKey(moderation)
  if (statusKey === null) return status
  // Подзаголовок раздела — как «Доступ к курсу» ниже: окно читается двумя
  // разделами, «Обновление» и «Доступ», а не набором карточек.
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{m.updateSection}</Label>
      <SmoothHeight stateKey={statusKey}>{status}</SmoothHeight>
    </div>
  )
}
